import { useCallback, useEffect, useState } from 'react';
import { estEchafaudage, ETIQUETTE_ECHAFAUDAGE, type OrigineEchafaudage } from '../../src/partage/echafaudage.js';
import { api, ErreurApi, type AnomalieFile, type CodeDecisionAnomalie, type SyntheseAnomalies } from './api';
import { Icone } from './icones';

/**
 * LE CENTRE DE CONTRÔLE DES ANOMALIES (§19).
 *
 * ⚠️ CE N'EST PAS UNE LISTE D'ERREURS, et c'est toute la différence. Chaque
 * ligne répond à six questions dans l'ordre où un agent se les pose :
 * QUELLE anomalie · POURQUOI · QUEL RISQUE · QUI doit agir · QUELLE action ·
 * QUEL IMPACT sur le dossier. Une file qui n'affiche que « recouvrement
 * gravité 3 » laisse l'agent devant un code : il ne sait ni ce qu'il risque,
 * ni si c'est à lui d'agir, ni ce qu'on attend de lui. Les 76 anomalies
 * ouvertes du registre étaient dans cet état — comptées par le plan de
 * travail, lisibles nulle part.
 *
 * ⚠️ LES PHRASES VIENNENT DU SERVICE, JAMAIS D'ICI. `explication`,
 * `impact.lecture`, `echeance.fondement` sont rendus par
 * `src/partage/anomalies.ts` et `src/domaine/file-anomalies.ts`. Les
 * recomposer à l'écran produirait une seconde version de la règle, qui
 * divergerait au premier correctif appliqué d'un seul côté.
 *
 * ⚠️ ET L'ÉCRAN AFFICHE CE QUE LA FILE NE VOIT PAS. La synthèse porte
 * `conformes_aveugles` : des verdicts « conforme » rendus après avoir examiné
 * ZÉRO candidat (59 468 pour `lacune`, 59 528 pour `recouvrement`, mesurés le
 * 12/09/2026). Un centre de contrôle qui les compte comme des conformités
 * donne d'un registre jamais comparé l'image d'un registre sain.
 */

const CLASSE_GRAVITE: Record<number, string> = { 3: 'rouge', 2: 'ambre', 1: 'gris' };

function dateFr(v: unknown): string {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('fr-FR');
}

/**
 * LA FORME DE TRAITEMENT — une décision motivée, trois issues.
 *
 * ⚠️ EXPORTÉE, PARCE QUE DEUX ÉCRANS LA MONTRENT : la file (ici) et la
 * chronologie juridique d'une parcelle (`procedures.tsx`). Deux formulaires
 * pour la même décision auraient divergé sur la longueur du motif exigée — et
 * l'un des deux aurait fait échouer la base sans que l'agent comprenne
 * pourquoi.
 */
export function FormeTraitementAnomalie({
  anomalie, onFait, onAnnuler,
}: {
  anomalie: { id: string; regle: string; explication: { libelle: string; limiteConnue?: string } | null };
  onFait: (message: string) => void;
  onAnnuler: () => void;
}) {
  const [decision, setDecision] = useState<CodeDecisionAnomalie | ''>('');
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null);
    if (!decision) { setErreur('Choisissez ce qui a été décidé.'); return; }
    // ⚠️ On valide ICI la longueur que la BASE exige (contrainte
    // `controle_traitement_motive`, ≥ 10 caractères) : faire l'aller-retour
    // réseau pour l'apprendre est un refus que l'agent lit comme une panne.
    if (motif.trim().length < 10) {
      setErreur('Le motif doit compter au moins 10 caractères : il sera relu dans six mois par quelqu’un d’autre.');
      return;
    }
    setEnCours(true);
    try {
      const r = await api.anomalies.traiter(anomalie.id, { decision, motif: motif.trim() });
      onFait(r.message + (r.suite_attendue ? ` — ${r.suite_attendue}` : ''));
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  return (
    <form className="traitement-anomalie" onSubmit={(e) => void soumettre(e)}>
      <p className="aide">
        Trois issues, qui ne veulent pas dire la même chose. Le motif reste au journal, sous votre nom.
      </p>
      <div className="tr-choix">
        {([
          ['corrigee', 'Corrigée', 'Le registre a changé : géométrie reprise, surface rectifiée, division versée.'],
          ['faux_positif', 'Faux positif', 'Le registre n’a pas changé : c’est la RÈGLE qui est en cause. Dites laquelle.'],
          ['acceptee_avec_reserve', 'Acceptée avec réserve', 'L’écart est réel et assumé. Vous en portez la responsabilité.'],
        ] as const).map(([code, libelle, sens]) => (
          <label key={code} className={decision === code ? 'tr-option actif' : 'tr-option'}>
            <input type="radio" name={`d-${anomalie.id}`} value={code}
                   checked={decision === code} disabled={enCours}
                   onChange={() => setDecision(code)} />
            <span><strong>{libelle}</strong><em>{sens}</em></span>
          </label>
        ))}
      </div>

      {/* La limite CONNUE de la règle est rappelée AVANT le motif : c'est elle
          qui autorise un agent à conclure « faux positif » sans que ce soit un
          abandon. La cacher le ferait hésiter, ou cocher au hasard. */}
      {decision === 'faux_positif' && anomalie.explication?.limiteConnue && (
        <div className="avertissement">{anomalie.explication.limiteConnue}</div>
      )}

      <label className="champ">
        <span className="champ-label">Motif <span className="aide">(obligatoire, ≥ 10 caractères)</span></span>
        <textarea value={motif} rows={3} disabled={enCours}
                  placeholder="Ex. : parcelle à 0,0 m de la limite de région — ST_Contains exclut les points de frontière."
                  onChange={(e) => setMotif(e.target.value)} />
      </label>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      <div className="actions-ligne">
        <button type="button" className="bouton discret" onClick={onAnnuler} disabled={enCours}>Annuler</button>
        <button type="submit" className="bouton" disabled={enCours}>
          {enCours ? 'Enregistrement…' : 'Inscrire la décision'}
        </button>
      </div>
    </form>
  );
}

/** Le détail d'une anomalie : les six questions, dans l'ordre. */
export function DetailAnomalie({ a }: { a: AnomalieFile }) {
  return (
    <div className="an-detail">
      {a.explication ? (
        <>
          <div className="an-question">
            <span className="an-q">Pourquoi</span>
            <p>{a.explication.pourquoi}</p>
          </div>
          <div className="an-question">
            <span className="an-q">Quel risque</span>
            <p>{a.explication.risque}</p>
          </div>
          <div className="an-question">
            <span className="an-q">Qui doit agir</span>
            <p><strong>{a.explication.service}</strong></p>
          </div>
          <div className="an-question">
            <span className="an-q">Action attendue</span>
            <p>{a.explication.action}</p>
          </div>
          {a.explication.limiteConnue && (
            <div className="avertissement">{a.explication.limiteConnue}</div>
          )}
        </>
      ) : (
        <div className="avertissement danger">{a.inexpliquee}</div>
      )}

      <div className="an-question">
        <span className="an-q">Impact sur le dossier</span>
        <p>{a.impact.lecture}</p>
        {a.impact.affaire && (
          <p className="aide">Dossier <code>{a.impact.affaire}</code> — étape {a.impact.affaire_etape}.</p>
        )}
      </div>

      <div className="an-question">
        <span className="an-q">Délai</span>
        {a.echeance ? (
          <p className={a.echeance.jours_restants < 0 ? 'depasse' : undefined}>
            {a.echeance.jours_restants < 0
              ? `Dépassé de ${-a.echeance.jours_restants} jour(s) — échéance ${dateFr(a.echeance.le)}.`
              : `${a.echeance.jours_restants} jour(s) restants — échéance ${dateFr(a.echeance.le)}.`}
            <br /><span className="aide">{a.echeance.fondement}</span>
          </p>
        ) : (
          /* ⚠️ L'ABSENCE DE DÉLAI EST DITE, jamais laissée vide : une case vide
             se lit « pas de délai » aussi bien que « délai inconnu ». Et aucun
             « SLA » n'est inventé — le seul délai opposable est celui de la
             procédure bloquée. */
          <p className="aide">{a.echeance_absente}</p>
        )}
      </div>

      {/* Le témoin de population : ce que la règle a réellement examiné. */}
      <div className="an-question">
        <span className="an-q">Ce que la règle a comparé</span>
        <p className={a.candidats_examines === 0 ? 'depasse' : undefined}>
          {a.candidats_examines === 0
            ? "ZÉRO candidat examiné : ce verdict n'a rien comparé. Il ne dit pas que le fonds est sain."
            : `${a.candidats_examines} objet(s) examiné(s).`}
        </p>
      </div>

      {a.objet.echafaudage && (
        <div className="an-question">
          <span className="an-q">Nature de l'objet</span>
          <p>{a.objet.echafaudage}</p>
        </div>
      )}

      {a.detail && Object.keys(a.detail).length > 0 && (
        <pre className="formulaire-brut">{JSON.stringify(a.detail, null, 2)}</pre>
      )}

      {a.traitement && (
        <div className={a.traitement.par_le_service ? 'an-traite service' : 'an-traite'}>
          <strong>{a.traitement.decision_libelle ?? 'Traité'}</strong>
          <span className="aide"> le {dateFr(a.traitement.le)} par {a.traitement.par}</span>
          {a.traitement.motif ? <p>{a.traitement.motif}</p> : null}
          {a.traitement.anterieur && <p className="avertissement">{a.traitement.anterieur}</p>}
        </div>
      )}
    </div>
  );
}

export function Anomalies({ peutTraiter, onOuvrirParcelle }: {
  peutTraiter: boolean;
  onOuvrirParcelle: (identifiant: string) => void;
}) {
  const [synthese, setSynthese] = useState<SyntheseAnomalies | null>(null);
  const [liste, setListe] = useState<AnomalieFile[]>([]);
  const [tronque, setTronque] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [etat, setEtat] = useState<'ouvertes' | 'traitees' | 'toutes'>('ouvertes');
  const [gravite, setGravite] = useState('');
  const [regle, setRegle] = useState('');
  const [bloquantes, setBloquantes] = useState(false);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [enTraitement, setEnTraitement] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    const q = new URLSearchParams({ etat, limite: '50' });
    if (gravite) q.set('gravite', gravite);
    if (regle) q.set('regle', regle);
    if (bloquantes) q.set('bloquantes', 'true');
    try {
      const [s, r] = await Promise.all([
        api.anomalies.synthese().catch(() => null),
        api.anomalies.liste(q),
      ]);
      if (s) setSynthese(s);
      setListe(r.anomalies ?? []);
      setTronque(r.tronque);
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter les contrôles de cohérence (cad.controle:lire)."
        : String((e as Error).message ?? e));
    }
  }, [etat, gravite, regle, bloquantes]);

  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Anomalies de cohérence</h2>
        <div className="actions">
          <label className="bascule">
            <input type="checkbox" checked={bloquantes} onChange={(e) => setBloquantes(e.target.checked)} />
            Bloquantes seulement
          </label>
          <select value={etat} onChange={(e) => setEtat(e.target.value as typeof etat)} aria-label="État">
            <option value="ouvertes">Ouvertes</option>
            <option value="traitees">Traitées</option>
            <option value="toutes">Toutes</option>
          </select>
          <select value={gravite} onChange={(e) => setGravite(e.target.value)} aria-label="Gravité">
            <option value="">Toutes gravités</option>
            <option value="3">Gravité 3 — bloquante</option>
            <option value="2">Gravité 2</option>
            <option value="1">Gravité 1</option>
          </select>
          <button className="bouton discret" onClick={() => void charger()}>Rafraîchir</button>
        </div>
      </header>

      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {synthese && (
        <>
          <div className="chiffres">
            <Chiffre valeur={synthese.ouvertes} libelle="ouvertes" base={`sur ${synthese.population} verdicts`} />
            <Chiffre valeur={synthese.bloquantes} libelle="bloquantes" base="gravité 3 — arrêtent tout transfert"
                     alerte={synthese.bloquantes > 0} />
            {/* ⚠️ « TRAITÉE » RECOUVRAIT DEUX CHOSES QUI N'ENGAGENT PAS LES
                MÊMES PERSONNES : la décision d'un agent, et la clôture
                automatique par un contrôle plus récent. Les additionner
                faisait paraître la file pleine de décisions que personne
                n'avait prises. */}
            <Chiffre valeur={synthese.decidees} libelle="décidées"
                     base="un agent en porte la responsabilité" />
            <Chiffre valeur={synthese.remplacees} libelle="remplacées"
                     base="closes par un contrôle plus récent — personne n'a décidé" />
            {/* ⚠️ LE CHIFFRE QUI MANQUE PARTOUT AILLEURS : ce que la file ne
                voit pas. Sans lui, « peu d'anomalies » se lit « registre sain ». */}
            <Chiffre valeur={synthese.conformes_aveugles} libelle="verdicts aveugles"
                     base="« conforme » sans avoir comparé un seul candidat"
                     alerte={synthese.conformes_aveugles > 0} />
            <Chiffre valeur={synthese.non_evaluables} libelle="non évaluables"
                     base="ni conformité ni anomalie" />
          </div>
          <p className="mention">{synthese.lecture}</p>
          {synthese.avertissement_portee && <div className="avertissement">{synthese.avertissement_portee}</div>}
          {synthese.closes_sans_decision > 0 && (
            <div className="avertissement">
              {synthese.closes_sans_decision} anomalie(s) ont été closes avant que la décision et son
              motif ne soient exigés (migration 076) : on ne peut pas savoir ce qui a été décidé, ni par qui.
              Le journal reste en ajout seul — ces lignes ne sont pas réécrites.
            </div>
          )}
          {synthese.regles_inexpliquees?.length && (
            <div className="avertissement danger">
              Règle(s) détectée(s) et non expliquée(s) : {synthese.regles_inexpliquees.join(', ')}.
              L'écran ne peut pas dire quoi faire — à consigner dans <code>src/partage/anomalies.ts</code>.
            </div>
          )}

          <div className="an-regles">
            <button className={regle === '' ? 'bouton discret actif' : 'bouton discret'}
                    onClick={() => setRegle('')}>Toutes les règles</button>
            {synthese.par_regle.map((r) => (
              <button key={`${r.regle}-${r.gravite}`}
                      className={regle === r.regle ? 'bouton discret actif' : 'bouton discret'}
                      onClick={() => setRegle(r.regle)}
                      title={r.fiche?.risque ?? r.inexpliquee ?? ''}>
                {r.fiche?.libelle ?? r.regle}
                <span className={`etat ${CLASSE_GRAVITE[r.gravite ?? 0] ?? 'gris'}`}>{r.ouvertes}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {liste.length === 0 ? (
        /* ⚠️ « AUCUNE ANOMALIE » N'EST PAS « REGISTRE SAIN », et la nuance est
           dite ici : la population est rappelée dans la même phrase. */
        <p className="rien-a-signaler">
          Aucune anomalie {etat === 'ouvertes' ? 'ouverte' : etat} avec ces filtres.
          {synthese ? ` Le registre porte ${synthese.population} verdicts, dont ${synthese.conformes_aveugles} rendus sans aucun candidat à comparer.` : ''}
        </p>
      ) : (
        <ul className="an-file">
          {liste.map((a) => (
            <li key={a.id} className={a.bloquante ? 'an-ligne bloquante' : 'an-ligne'}>
              <div className="an-tete">
                <span className={`etat ${CLASSE_GRAVITE[a.gravite ?? 0] ?? 'gris'}`}>
                  {a.bloquante ? 'BLOQUANTE' : `gravité ${a.gravite ?? '—'}`}
                </span>
                <strong>{a.explication?.libelle ?? a.regle}</strong>
                {a.objet.parcelle ? (
                  <button className="lien" onClick={() => onOuvrirParcelle(a.objet.parcelle!)}>
                    <code>{a.objet.parcelle}</code>
                    <Icone nom="ouvrir" />
                  </button>
                ) : (
                  <span className="aide">{a.objet.non_situable ?? a.objet.releve ?? '—'}</span>
                )}
                {a.objet.division && <span className="aide">{a.objet.division}</span>}
                {/* ⚠️ CE QUI DÉPARTAGE DEUX CONFLITS FONCIERS DE NEUF RESTES DE
                    QUADRILLAGE. Sans cette étiquette, « 11 recouvrements
                    bloquants » envoie chercher onze conflits là où il y en a
                    deux — mesuré le 12/09/2026. */}
                {estEchafaudage(a.objet.parcelle_origine) && (
                  <span className="etat gris" title={a.objet.echafaudage}>
                    {ETIQUETTE_ECHAFAUDAGE[a.objet.parcelle_origine as OrigineEchafaudage]}
                  </span>
                )}
                {/* ⚠️ « il y a 0 j » se lisait comme un compteur cassé — vu en
                    ouvrant la page, invisible à tout essai. Le jour même se
                    dit « aujourd'hui ». */}
                <span className="aide">
                  {a.age_jours === 0 ? 'détectée aujourd’hui'
                    : a.age_jours === 1 ? 'détectée hier'
                    : `détectée il y a ${a.age_jours} jours`}
                </span>
                {a.echeance && a.echeance.jours_restants < 0 && (
                  <span className="etat rouge">délai dépassé</span>
                )}
                <button className="bouton discret"
                        onClick={() => setOuverte(ouverte === a.id ? null : a.id)}>
                  {ouverte === a.id ? 'Replier' : 'Expliquer'}
                </button>
                {peutTraiter && !a.traitement && (
                  <button className="bouton" onClick={() => setEnTraitement(a.id)}>Traiter</button>
                )}
              </div>

              <p className="an-impact">{a.impact.lecture}</p>

              {ouverte === a.id && <DetailAnomalie a={a} />}

              {enTraitement === a.id && (
                <FormeTraitementAnomalie
                  anomalie={a}
                  onAnnuler={() => setEnTraitement(null)}
                  onFait={(m) => { setEnTraitement(null); setMessage(m); void charger(); }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {tronque && (
        <p className="mention">
          Liste tronquée à 50 lignes. Affinez les filtres — une liste tronquée qui ne s'annonce pas
          se lit comme une liste complète.
        </p>
      )}

      {!peutTraiter && liste.length > 0 && (
        <p className="masque">
          Votre rôle ne permet pas d'inscrire une décision (<code>cad.controle:traiter</code>) :
          vous lisez la file, vous ne la refermez pas.
        </p>
      )}
    </div>
  );
}

function Chiffre({ valeur, libelle, base, alerte }: {
  valeur: number; libelle: string; base: string; alerte?: boolean;
}) {
  return (
    <div className={alerte ? 'chiffre alerte' : 'chiffre'}>
      <div className="chiffre-valeur nombre">{valeur.toLocaleString('fr-FR')}</div>
      <div className="chiffre-libelle">{libelle}</div>
      <div className="chiffre-base">{base}</div>
    </div>
  );
}
